import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { categories, channels, searchHotKeywords } from "@/mocks/streams";
import StreamCard from "@/components/feature/StreamCard";
import FilterBar from "@/components/feature/FilterBar";
import type { FilterState } from "@/components/feature/FilterBar";
import { filterAndSortChannels, matchKeyword } from "@/lib/filterChannels";
import { usePageMeta } from "@/hooks/usePageMeta";

const defaultFilters: FilterState = { status: "all", level: "all", language: "all", sort: "popularity" };

function categoryNameOf(categoryId: string): string {
  return categories.find((cat) => cat.id === categoryId)?.name ?? "";
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [visibleCount, setVisibleCount] = useState(8);

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 有因直播` : "搜索直播 - 有因直播",
    keyword
      ? `有因直播为你找到与“${keyword}”相关的直播频道，覆盖主播、直播内容与分类关键词。`
      : "在有因直播搜索主播、直播标题与内容关键词，快速找到你感兴趣的直播频道。",
  );

  useEffect(() => {
    setInput(keyword);
    setVisibleCount(8);
    setFilters(defaultFilters);
  }, [keyword]);

  const matched = useMemo(() => {
    if (!keyword.trim()) return [];
    return channels.filter((channel) => matchKeyword(channel, keyword, categoryNameOf(channel.categoryId)));
  }, [keyword]);

  const filtered = useMemo(() => filterAndSortChannels([...matched], filters), [matched, filters]);

  useEffect(() => {
    setVisibleCount(8);
  }, [filters]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (trimmed) setSearchParams({ q: trimmed });
    else setSearchParams({});
  };

  const updateFilters = (next: Partial<FilterState>) => setFilters((prev) => ({ ...prev, ...next }));
  const resetFilters = () => setFilters(defaultFilters);

  const recommendations = channels.filter((channel) => channel.isLive).sort((a, b) => b.viewers - a.viewers).slice(0, 3);
  const visible = filtered.slice(0, visibleCount);

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <section className="rounded-xl border border-background-200/70 bg-background-100 p-5 md:p-8">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">搜索直播</h1>
        <p className="mt-2 text-sm text-foreground-400">输入主播昵称、直播标题或内容关键词，找到你感兴趣的直播间</p>

        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center rounded-md border border-background-200 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="例如：王者荣耀、民谣弹唱、深夜电台"
              className="w-full bg-transparent px-2 text-sm text-foreground-800 placeholder-foreground-300 outline-none"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热门搜索：</span>
          {searchHotKeywords.map((word) => (
            <Link
              key={word}
              to={`/search?q=${encodeURIComponent(word)}`}
              className="rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-500 transition-colors hover:text-primary-500 whitespace-nowrap"
            >
              {word}
            </Link>
          ))}
        </div>
      </section>

      {!keyword.trim() ? (
        <section className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">正在热播的直播</h2>
          <p className="mt-1 text-sm text-foreground-400">还没想好搜什么？先看看大家正在看的内容</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {recommendations.concat(channels.slice(0, 5)).slice(0, 8).map((channel) => (
              <StreamCard key={channel.id} channel={channel} />
            ))}
          </div>
        </section>
      ) : (
        <>
          <div className="mt-6">
            <FilterBar filters={filters} onChange={updateFilters} onReset={resetFilters} />
          </div>

          <p className="mt-6 text-sm text-foreground-400">
            关键词「<span className="text-foreground-900">{keyword}</span>」共找到{" "}
            <span className="text-primary-500">{filtered.length}</span> 个相关直播
          </p>

          {filtered.length === 0 ? (
            <div className="mt-4 rounded-lg border border-background-200/70 bg-background-100 p-10 text-center">
              <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有找到相关直播</h2>
              <p className="mt-2 text-sm text-foreground-400">换一个关键词试试，或者先看看下面推荐的热门直播。</p>
              <button
                type="button"
                onClick={() => {
                  setSearchParams({});
                  setFilters(defaultFilters);
                }}
                className="mt-5 inline-flex rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                返回热门直播
              </button>

              <div className="mt-8 text-left">
                <h3 className="mb-4 font-heading text-sm font-semibold text-foreground-900">为你推荐</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {recommendations.map((channel) => (
                    <StreamCard key={channel.id} channel={channel} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visible.map((channel) => (
                  <StreamCard key={channel.id} channel={channel} />
                ))}
              </div>
              {visibleCount < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + 8)}
                    className="flex items-center gap-2 rounded-full border border-background-300 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
                  >
                    加载更多
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}